import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  modifyModalRecoil,
  addressModalRecoil,
  devicePlusModalRecoil,
  coordinateModalRecoil,
  facilitiesSearchModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import { DeviceApi } from '@/sdk/DeviceApi';
import { UploadApi } from '@/sdk/UploadApi';
import { ModelApi } from '@/sdk/ModelApi';
import { FacilityApi } from '@/sdk/FacilityApi';
import ModalType1 from '@/modal/ModalType1';
import AddressModal from './AddressModal';
import FacilitiesSearchModal from './FacilitiesSearchModal';
import {
  FileUploadDto,
  GetFacilitiesDtoRx,
  CreateDeviceDtoTx,
  Calibration,
  FacilityControllerGetFacilitiesParams,
} from '@/sdk/data-contracts';
import {
  deviceAddModelListRecoil,
  deviceAddFacilitiesRecoil,
} from '@/atom/DeviceAtom';
import CoordinateModal from './CoordinateModal';
import { facilitiesListRecoil } from '@/atom/FacilitiesAtom';

interface Coordinate {
  lat: number;
  lng: number;
}

const DevicePlusModal = () => {
  const { id } = useParams<{ id: string }>();
  const uploadApi = new UploadApi({ baseURL: Config.API_URL });
  const deviceApi = new DeviceApi({ baseURL: Config.API_URL });
  const modelApi = new ModelApi({ baseURL: Config.API_URL });
  const facilityApi = new FacilityApi({ baseURL: Config.API_URL });

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const setDevicePlusModalState = useSetRecoilState(devicePlusModalRecoil);
  const [addressModalState, setAddressModalState] =
    useRecoilState(addressModalRecoil);
  const [facilitiesSearchModalState, setFacilitiesSearchModalState] =
    useRecoilState(facilitiesSearchModalRecoil);
  const [deviceAddModelListState, setDeviceAddModelListState] = useRecoilState(
    deviceAddModelListRecoil,
  );
  const [deviceAddFacilitiesState, setDeviceAddFacilitiesState] =
    useRecoilState(deviceAddFacilitiesRecoil);
  const [coordinateModalState, setCoordinateModalState] = useRecoilState(
    coordinateModalRecoil,
  );
  const [facilitiesListState, setFacilitiesListState] =
    useRecoilState(facilitiesListRecoil);

  const [modelId, setModelId] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [number, setNumber] = useState<string>('');
  const [devEui, setDevEui] = useState<string>('');
  const [encKey, setEncKey] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [fileUrl, setFileUrl] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  const [facilityId, setFacilityId] = useState<string>('');
  const [facilityName, setFacilityName] = useState<string>('');
  const [facilityAddress, setFacilityAddress] = useState<string>('');
  const [selectFacility, setSelectFacility] = useState<GetFacilitiesDtoRx>();
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [setUpDate, setSetUpDate] = useState<string>('');
  const [imageName, setImageName] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [calibration, setCalibration] = useState<Calibration[]>([]);
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imgInputRef = useRef<HTMLInputElement | null>(null);

  const fetchModelList = async () => {
    try {
      const { data } = await deviceApi.deviceControllerGetModelList({
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setDeviceAddModelListState(data);
    } catch (err: any) {}
  };

  const fetchFacilitiesList = async () => {
    const dto: FacilityControllerGetFacilitiesParams = {
      sortBy,
      order,
    };
    try {
      const { data } = await facilityApi.facilityControllerGetFacilities(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      if (data.results) {
        setFacilitiesListState(data.results);
        return data.results;
      }
    } catch (err: any) {}
  };

  const handleModelChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setModelId(e.target.value);
    try {
      const { data } = await modelApi.modelControllerGetModel(
        Number(e.target.value),
        {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        },
      );
      const calibrationData = Object.keys(data.dataInformation).map((key) => ({
        name: key,
        formula: '',
      }));
      setCalibration(calibrationData);
    } catch (err: any) {}
  };

  const handleFileUploadBtnClick = (type: string) => {
    if (type === 'file') {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
    if (type === 'img') {
      if (imgInputRef.current) {
        imgInputRef.current.click();
      }
    }
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    if (e.target.files) {
      const dto: FileUploadDto = {
        file: e.target.files[0],
      };
      try {
        const { data } = await uploadApi.fileuploadControllerUploadFile(dto);
        if (type === 'file') {
          setFileName(e.target.files[0].name);
          setFileUrl(String(data.file));
        }
        if (type === 'img') {
          setImageName(e.target.files[0].name);
          setImageUrl(String(data.file));
        }
      } catch (err: any) {}
    }
  };

  const handleFileDel = (type: string) => {
    if (type === 'file') {
      setFileName('');
      setFileUrl('');
    }
    if (type === 'img') {
      setImageName('');
      setImageUrl('');
    }
  };

  const handleFacilitiesSelect = (facilities: GetFacilitiesDtoRx) => {
    setFacilityId(String(facilities.id));
    setFacilityName(String(facilities.name));
    setFacilityAddress(String(facilities.address));
  };

  const handleCoordinateSelect = (coordinate: Coordinate) => {
    setLatitude(String(coordinate.lat));
    setLongitude(String(coordinate.lng));
  };

  const handleCorrectionValueChange = (
    index: number,
    field: keyof Calibration,
    value: string,
  ) => {
    setCalibration((prevCalibration) =>
      prevCalibration.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleSubmit = async () => {
    if (
      modelId === '' ||
      name === '' ||
      number === '' ||
      devEui === '' ||
      facilityId === '' ||
      latitude === '' ||
      longitude === '' ||
      location === '' ||
      setUpDate === ''
    ) {
      setErrorModalText('빈 칸을 확인해 주세요.');
      return;
    }

    const dto: CreateDeviceDtoTx = {
      modelId: Number(modelId),
      name,
      number,
      devEui,
      encKey,
      fileName,
      fileUrl,
      memo,
      facilityId: Number(facilityId),
      latitude: Number(latitude),
      longitude: Number(longitude),
      location,
      setUpDate,
      imageName,
      imageUrl,
      calibration,
    };

    try {
      await deviceApi.deviceControllerCreateDevice(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchModelList();
    const getFacilities = async () => {
      await fetchFacilitiesList();
      const facilitiesList = await fetchFacilitiesList();
      const facilities = facilitiesList?.find((list) => list.id === Number(id));
      if (facilities) {
        setSelectFacility(facilities);
        setFacilityId(String(facilities?.id));
        setFacilityName(String(facilities?.name));
        setFacilityAddress(String(facilities?.address));
      }
    };

    getFacilities();
  }, []);

  return (
    <>
      <ModalType1 width={1000}>
        <div className="title-box flex aic jcsb">
          <h1>디바이스 추가</h1>
          <button
            className="x-btn img-box"
            onClick={() => setDevicePlusModalState(false)}
          >
            <img src="/img/ico_x_444.svg" alt="" />
          </button>
        </div>
        <div className="cnt-area flex fdc">
          <div className="cnt-wrap flex fdc">
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">기본 정보</h2>
                </div>
                <div className="right-box flex">
                  <h3>
                    <span className="inblock">*</span>필수 항목
                  </h3>
                </div>
              </div>
              <CS.TableType2>
                <table>
                  <tbody>
                    <tr>
                      <th>
                        모델 선택
                        <span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.SelectType1 style={{ width: 190 }}>
                          <select
                            value={modelId}
                            onChange={(e) => handleModelChange(e)}
                            className="selected"
                          >
                            <option value="">디바이스 모델 선택</option>
                            {deviceAddModelListState.map((list) => (
                              <option key={list.id} value={list.id}>
                                {list.name}
                              </option>
                            ))}
                          </select>
                          <span className="img-box">
                            <img src="/img/arrow_bottom_555.svg" alt="" />
                          </span>
                        </IS.SelectType1>
                      </td>
                      <th>
                        디바이스명<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        디바이스 No<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={number}
                          onChange={(e) => setNumber(e.target.value)}
                        />
                      </td>
                      <th>
                        Dev EUI<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={devEui}
                          onChange={(e) => setDevEui(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>encKey</th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 488 }}
                          type="text"
                          value={encKey}
                          onChange={(e) => setEncKey(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>첨부파일</th>
                      <td colSpan={3}>
                        <div className="td-box">
                          <BS.Btn3Type5
                            type="button"
                            onClick={() => handleFileUploadBtnClick('file')}
                          >
                            파일 선택
                          </BS.Btn3Type5>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={(e) => handleFileChange(e, 'file')}
                            style={{ display: 'none' }}
                          />
                          <IS.FileTextType1>
                            {fileName === '' ? (
                              <span className="no-file-text">
                                선택된 파일 없음
                              </span>
                            ) : (
                              <span className="file-text">{fileName}</span>
                            )}
                            {fileName !== '' && (
                              <button
                                className="img-box"
                                type="button"
                                onClick={() => handleFileDel('file')}
                              >
                                <img src="/img/ico_x_FC5B5B.svg" alt="" />
                              </button>
                            )}
                          </IS.FileTextType1>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>메모</th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 488 }}
                          type="text"
                          value={memo}
                          onChange={(e) => setMemo(e.target.value)}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CS.TableType2>
            </div>
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">설치 정보</h2>
                </div>
              </div>
              <CS.TableType2>
                <table>
                  <tbody>
                    <tr>
                      <th>대상 시설물</th>
                      <td>
                        <div className="td-wrap">
                          <div className="td-box">
                            <IS.TextInputType1
                              style={{ width: 482 }}
                              type="text"
                              disabled={true}
                              value={facilityName}
                            />
                          </div>
                          <div className="td-box">
                            <IS.TextInputType1
                              style={{ width: 482 }}
                              type="text"
                              disabled={true}
                              value={facilityAddress}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        설치 위치<span className="star-point">*</span>
                      </th>
                      <td colSpan={3}>
                        <div className="td-box">
                          <IS.TextInputType1
                            style={{ width: 190 }}
                            type="text"
                            placeholder="위도"
                            value={latitude}
                            disabled={true}
                          />
                          <IS.TextInputType1
                            style={{ width: 190 }}
                            type="text"
                            placeholder="경도"
                            value={longitude}
                            disabled={true}
                          />
                          <BS.Btn3Type1
                            type="button"
                            onClick={() => {
                              if (facilityId === '') {
                                setErrorModalText(
                                  '시설물을 먼저 선택해 주세요.',
                                );
                                return;
                              }
                              setCoordinateModalState(true);
                            }}
                          >
                            위치지정
                          </BS.Btn3Type1>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        설치 위치명<span className="star-point">*</span>
                      </th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 488 }}
                          type="text"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        설치 일자<span className="star-point">*</span>
                      </th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="date"
                          value={setUpDate}
                          onChange={(e) => setSetUpDate(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>도면/이미지</th>
                      <td colSpan={3}>
                        <div className="td-box">
                          <BS.Btn3Type5
                            type="button"
                            onClick={() => handleFileUploadBtnClick('img')}
                          >
                            파일 선택
                          </BS.Btn3Type5>
                          <input
                            type="file"
                            ref={imgInputRef}
                            onChange={(e) => handleFileChange(e, 'img')}
                            style={{ display: 'none' }}
                          />
                          <IS.FileTextType1>
                            {imageName === '' ? (
                              <span className="no-file-text">
                                선택된 파일 없음
                              </span>
                            ) : (
                              <span className="file-text">{imageName}</span>
                            )}
                            {imageName !== '' && (
                              <button
                                className="img-box"
                                type="button"
                                onClick={() => handleFileDel('img')}
                              >
                                <img src="/img/ico_x_FC5B5B.svg" alt="" />
                              </button>
                            )}
                          </IS.FileTextType1>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CS.TableType2>
            </div>
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">교정값 설정</h2>
                </div>
              </div>
              <CS.TableType1>
                <table>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>데이터명</th>
                      <th>계산식</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calibration.map((list, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{list.name}</td>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 604 }}
                            type="text"
                            value={list.formula}
                            onChange={(e) =>
                              handleCorrectionValueChange(
                                index,
                                'formula',
                                e.target.value,
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CS.TableType1>
            </div>
          </div>
          <div className="btn-wrap">
            <BS.Btn2Type3
              type="button"
              onClick={() => setDevicePlusModalState(false)}
            >
              취소
            </BS.Btn2Type3>
            <BS.Btn2Type4 type="button" onClick={handleSubmit}>
              등록
            </BS.Btn2Type4>
          </div>
        </div>
      </ModalType1>
      {coordinateModalState && (
        <CoordinateModal
          onCoordinateSelect={handleCoordinateSelect}
          addFacilities={selectFacility}
          facilitiesName={facilityName}
          facilitiesAddress={facilityAddress}
          latitude={latitude}
          longitude={longitude}
        />
      )}
    </>
  );
};

export default DevicePlusModal;
